/*
 * Copyright (c) Nexign, JSC, 2019-2023.
 */
/**
 *
 * @author Yakov Gushchin
 * @author Mikhael Kokoulin
 * @author Aleksandr Perkhunov
 * @author Anton Panasenko
 * @author Evgenij Tyan
 * @author Ilya Babanov
 * @author Andrey Dubov
 * @author Evgeniy Naumov
 * @author Alexander Golshtein
 * @version 1.25.0
 * @since 1.0.0
 */
import React, { Fragment, PureComponent, SyntheticEvent } from 'react';

import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import { Dispatch } from 'redux';
import { createStructuredSelector } from 'reselect';

import { makeSelectAuthSSO } from '@/main/modules/app/appSelectors';
import { EModeSSO, IAuthSSO } from '@/main/modules/app/appTypes';
import { getContextHelpText } from '@/main/pages/CorporatePage/CorporatePage';
import { redirectToSohoAction } from '@/main/pages/PageAuth/actions/authActions';
import {
    makeSelectAuthIsForbidden,
    makeSelectAuthoritiesIsServiceUser,
    makeSelectLoggedIn,
    makeSelectRedirectAllowed,
    makeSelectServiceAccessLoggedIn,
} from '@/main/pages/PageAuth/selectors/authSelectors';
import type { IRootReducer } from '@/main/reducers/rootReducer';
import { RoutePath } from '@/main/routes/constants/routesConstants';
import { icons } from '@assets/index';
import GTMOperations from '@modules/analytics/GoogleTagManager/GTMOperations';
import { makeSelectorBannerTotalCount } from '@modules/banner/bannerSelectors';
import { makeSelectTranslationCommon } from '@modules/internationalization/intSelectors';
import type ICommonTranslation from '@modules/internationalization/types/common';
import type ICommonTranslationContextHelp from '@modules/internationalization/types/common/contextHelp';
import type ICorporateTranslation from '@modules/internationalization/types/corporate';
import { ELogBusinessEventName, ELogBusinessEventType, logBusinessEvent } from '@modules/logging';
import { ILogBusinessEventPayload } from '@modules/logging/loggingTypes';
import { modalCloseAll as modalCloseAllAction, modalOpen } from '@modules/modal/modalActions';
import type { IModalOpenPayload } from '@modules/modal/types/modalActionTypes';
import type { IModalRenderProps } from '@modules/modal/types/modalTypes';
import NoticeMessage from '@modules/notification/components/NoticeMessage';
import { onboardingDisableAllAction, onboardingEnableAction } from '@modules/onboarding/onboardingActions';
import {
    makeSelectOnboardingIsActive,
    makeSelectOnboardingIsDisplayedByUser,
    makeSelectOnboardingIsOnboardingExists,
    makeSelectOnboardingIsWelcomeOnboardingCompleted,
} from '@modules/onboarding/onboardingSelectors';
import SwitchServiceUserDenied from '@modules/role/components/SwitchServiceUserDenied';
import { AccessComponentKeyEnum } from '@modules/role/constants/guard/guardConstants';
import Guard from '@modules/role/Guard';
import { icons as iconTheme } from '@theme/index';

import pjson from '../../../../package.json';
import RedirectLink from '../common/RedirectLink/RedirectLink';
import ArrowTooltip from '../common/Tooltip/Tooltip';
import CorporateHeader from '../CorporateHeader/CorporateHeader';
import HintPopup from '../HintPopup/HintPopup';
import { setHelpFlagAction } from './headerActions';
import { makeSelectorHelpFlag } from './headerSelectors';
import {
    HeaderContainerBottomStyled,
    HeaderContainerTopStyled,
    InfoBlockStyled,
    InfoLinkStyled,
    LogoContainerStyled,
    LogoImgStyled,
    LogoLabelStyled,
    MainHeaderContainerStyled,
    PageInfoStyled,
    SupportBlockStyled,
    SupportImageStyled,
} from './styled/HeaderStyled';

interface IHeaderState {
    isContextHelpOpen: boolean;
    isServiceUserPage: boolean;
    itemList: IHeaderTabItem[];
}

interface IHeaderProps extends RouteComponentProps {
    loggedIn: boolean;
    offerCounter: number;
    modalOpen: (payload: IModalOpenPayload) => void;
    setHelpFlag: (payload: boolean) => void;
}

interface IHeaderTabItem {
    selected: boolean;
    value: string;
}

interface IConnectedProps {
    helpFlag: boolean;
    isDisplayedByUser: boolean;
    isForbidden: boolean;
    isOnboardingActive: boolean;
    isOnboardingExists: boolean;
    isServiceAccessLoggedIn: boolean;
    isServiceUser: boolean;
    isWelcomeOnboardingCompleted: boolean;
    translation: ICommonTranslation & ICorporateTranslation;
    authSSO: IAuthSSO;
    isRedirectAllowed: boolean;
}

const TabListItems: IHeaderTabItem[] = [
    { value: 'corporate', selected: false },
    { value: 'target', selected: false },
    { value: 'vas', selected: false },
    { value: 'more', selected: false },
];

/**
 * Header component.
 */
class Header extends PureComponent<IHeaderProps & IConnectedProps & IDispatchProps, IHeaderState> {
    state = {
        isContextHelpOpen: false,
        isServiceUserPage: false,
        itemList: this.setItemState(TabListItems),
    };

    componentDidUpdate(prevProps: IHeaderProps) {
        if (prevProps.location.pathname !== this.props.location.pathname) {
            this.setState({
                itemList: this.setItemState(TabListItems),
            });
        }
        this.setState({
            isServiceUserPage: this.props.location.pathname === RoutePath.serviceAccessLogin,
        });
    }
    render() {
        const {
            helpFlag,
            isDisplayedByUser,
            isForbidden,
            isOnboardingActive,
            isOnboardingExists,
            isServiceAccessLoggedIn,
            isServiceUser,
            isWelcomeOnboardingCompleted,
            loggedIn,
            translation,
            authSSO,
            isRedirectAllowed,
        } = this.props;
        const { header } = this.props.translation;
        const { isServiceUserPage } = this.state;
        const contextHelpText = this.getContextHelpText(translation.contextHelp);

        return (
            <Fragment>
                <HeaderContainerTopStyled
                    style={{
                        zIndex: isOnboardingActive ? 1001 : 6,
                    }}
                >
                    {loggedIn && <NoticeMessage />}

                    <MainHeaderContainerStyled
                        style={{ justifyContent: !isServiceUserPage ? 'space-between' : 'flex-end' }}
                    >
                        {!isServiceUserPage && (
                            <LogoContainerStyled onClick={GTMOperations.navigation.onLogoClickOperation}>
                                <Link to="/">
                                    <LogoImgStyled>
                                        <img alt="" data-logo="logo" src={iconTheme.logoImage} />
                                    </LogoImgStyled>
                                    <LogoLabelStyled data-logo-label="logo-label">{header.logoLabel}</LogoLabelStyled>
                                </Link>
                            </LogoContainerStyled>
                        )}

                        <InfoBlockStyled data-ver={pjson.version}>
                            {!isServiceUserPage ? (
                                <a
                                    data-link="link-business"
                                    href={translation.bizLink}
                                    rel="noreferrer"
                                    target="_blank"
                                >
                                    <InfoLinkStyled>{header.info.link}</InfoLinkStyled>
                                </a>
                            ) : (
                                isServiceAccessLoggedIn && (
                                    <Link to={RoutePath.logout}>
                                        <InfoLinkStyled>{header.logout}</InfoLinkStyled>
                                    </Link>
                                )
                            )}
                            {loggedIn && !isForbidden && (
                                <SupportBlockStyled>
                                    <div className="support-block__item">
                                        <ArrowTooltip
                                            message={header.support}
                                            placement="bottom-end"
                                            title={translation.companyInfoPhone}
                                            onClick={() => {
                                                console.info(pjson.version);
                                            }}
                                        >
                                            <SupportImageStyled
                                                data-header-icon="iconSupport"
                                                height="32"
                                                src={icons.supportIcon}
                                                width="32"
                                            />
                                        </ArrowTooltip>
                                    </div>
                                    <div className="support-block__item">
                                        <div className="support-block__item-label">{header.iconHelp}</div>
                                        <ArrowTooltip
                                            disableHoverListener={
                                                !helpFlag ||
                                                !(header.iconHelpTooltip.text || header.iconHelpTooltip.title)
                                            }
                                            message={header.iconHelpTooltip.text}
                                            placement="bottom-end"
                                            title={header.iconHelpTooltip.title}
                                        >
                                            <PageInfoStyled
                                                className={contextHelpText ? 'has-info' : ''}
                                                data-header-icon="iconInfo"
                                                src={icons.infoBlackIcon}
                                                onClick={this.onPageInfoClick}
                                            />
                                        </ArrowTooltip>
                                    </div>
                                    {isOnboardingExists && isDisplayedByUser && (
                                        <Guard accessKey={AccessComponentKeyEnum.onboarding}>
                                            <ArrowTooltip
                                                maxWidth="200px"
                                                placement="bottom-end"
                                                title={!isServiceUser ? header.onboardingTooltip : ''}
                                            >
                                                <div className="support-block__item">
                                                    <div className="support-block__item-label">{header.onboarding}</div>
                                                    <SwitchServiceUserDenied
                                                        checked={!isWelcomeOnboardingCompleted}
                                                        placement="bottom-end"
                                                        onChange={(_: any, isChecked: any) =>
                                                            this.onChangeOnboarding(isChecked)
                                                        }
                                                    />
                                                </div>
                                            </ArrowTooltip>
                                        </Guard>
                                    )}
                                    {isRedirectAllowed && authSSO.mode === EModeSSO.soho && authSSO.redirect && (
                                        <div className="support-block__item">
                                            <RedirectLink link={authSSO.redirect} onClick={this.onRedirectToSohoClick}>
                                                <InfoLinkStyled>{header.linkToSoho}</InfoLinkStyled>
                                            </RedirectLink>
                                        </div>
                                    )}
                                </SupportBlockStyled>
                            )}
                        </InfoBlockStyled>
                    </MainHeaderContainerStyled>
                </HeaderContainerTopStyled>
                <HeaderContainerBottomStyled>
                    {loggedIn && !isForbidden && <CorporateHeader offerCounter={this.props.offerCounter} />}
                    {!loggedIn && <NoticeMessage />}
                </HeaderContainerBottomStyled>
            </Fragment>
        );
    }

    private onChangeOnboarding(isEnable: boolean) {
        if (isEnable) {
            this.props.onboardingEnable();
        } else {
            this.props.modalCloseAll();
            this.props.onboardingDisableAll();
        }
    }

    private setItemState(list: IHeaderTabItem[]): IHeaderTabItem[] {
        const currentPath = this.props.location.pathname;
        return list.map((item: IHeaderTabItem) => {
            item.selected = currentPath.includes(item.value);
            return item;
        });
    }

    /**
     * @description Get contextHelp text according to location.
     * @param {ICommonTranslationContextHelp} translation
     * @return {string}
     */
    private getContextHelpText = (translation: ICommonTranslationContextHelp) => {
        const { pathname } = this.props.location;
        return getContextHelpText(translation, pathname);
    };

    private onRedirectToSohoClick = (ev: SyntheticEvent) => {
        ev.preventDefault();
        this.props.logBusinessEvent({
            eventName: ELogBusinessEventName.USER_TO_SOHO_VERSION,
            type: ELogBusinessEventType.common,
        });
        this.props.redirectToSoho();
    };

    private onPageInfoClick = () => {
        const contextHelpText = this.getContextHelpText(this.props.translation.contextHelp);
        if (contextHelpText) {
            GTMOperations.navigation.tipsClickOperation();
            const contextHelpTranslation = this.props.translation.contextHelp;
            this.props.modalOpen({
                render: (props: IModalRenderProps) => (
                    <HintPopup
                        closeBtn={contextHelpTranslation.closeBtn}
                        handleClose={props.onCloseModal}
                        isOpen={false}
                        link={contextHelpTranslation.link}
                        text={contextHelpText}
                        title={contextHelpTranslation.title}
                        translations={this.props.translation}
                    />
                ),
            });
        }
    };
}

const mapStateToProps = () =>
    createStructuredSelector<IRootReducer, IHeaderProps, IConnectedProps | IHeaderProps>({
        helpFlag: makeSelectorHelpFlag(),
        isDisplayedByUser: makeSelectOnboardingIsDisplayedByUser(),
        isForbidden: makeSelectAuthIsForbidden(),
        isOnboardingActive: makeSelectOnboardingIsActive(),
        isOnboardingExists: makeSelectOnboardingIsOnboardingExists(),
        isServiceAccessLoggedIn: makeSelectServiceAccessLoggedIn(),
        isServiceUser: makeSelectAuthoritiesIsServiceUser(),
        isWelcomeOnboardingCompleted: makeSelectOnboardingIsWelcomeOnboardingCompleted(),
        loggedIn: makeSelectLoggedIn(),
        offerCounter: makeSelectorBannerTotalCount(),
        translation: makeSelectTranslationCommon(),
        authSSO: makeSelectAuthSSO(),
        isRedirectAllowed: makeSelectRedirectAllowed(),
    });

export interface IDispatchProps {
    modalCloseAll: () => void;
    modalOpen: (payload: IModalOpenPayload) => void;
    onboardingEnable: () => void;
    onboardingDisableAll: () => void;
    setHelpFlag: (payload: boolean) => void;
    redirectToSoho: () => void;
    logBusinessEvent: (payload: ILogBusinessEventPayload) => void;
}

const mapDispatchToProps = (dispatch: Dispatch): IDispatchProps => ({
    modalCloseAll: () => dispatch(modalCloseAllAction()),
    modalOpen: (payload: IModalOpenPayload) => dispatch(modalOpen(payload)),
    onboardingEnable: () => dispatch(onboardingEnableAction()),
    onboardingDisableAll: () => dispatch(onboardingDisableAllAction()),
    setHelpFlag: (payload: boolean) => dispatch(setHelpFlagAction(payload)),
    redirectToSoho: () => dispatch(redirectToSohoAction()),
    logBusinessEvent: (payload) => dispatch(logBusinessEvent(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export default withRouter(withConnect(Header)) as unknown as React.FC<any>;
